import { useRouter } from 'next/router';
import { useMemo } from 'react';
interface StyledPriceClassNames {
  /** Class for the decimal/fraction digits */
  decimal?: string;
  /** Class for the integer/main digits */
  integer?: string;
  /** Class for the decimal separator (e.g., . or ,) */
  separator?: string;
  /** Class for the currency symbol (e.g., $, £, €) */
  symbol?: string;
}
interface StyledPriceProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** ClassNames for individual parts */
  classNames?: StyledPriceClassNames;
  /** Currency code (e.g., "GBP", "USD", "EUR") */
  currency: string;
  /**
   * Custom screen reader text. If not provided, uses the
   * default formatted price string.
   */
  srText?: string;
  /** Price value in minor units (pence/cents) */
  valueInMinorUnits: number;
}
const StyledPrice = ({
  valueInMinorUnits,
  currency,
  classNames = {},
  srText,
  className,
  ...props
}: StyledPriceProps) => {
  const router = useRouter();
  const locale = router.locale ?? router.defaultLocale ?? 'en-US';
  const {
    parts,
    formattedPrice
  } = useMemo(() => {
    const convertedValue = valueInMinorUnits / 100;
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    });
    const formattedParts = formatter.formatToParts(convertedValue);
    const formatted = formatter.format(convertedValue);
    const symbol: string[] = [];
    const integer: string[] = [];
    const decimal: string[] = [];
    let separator = '';
    for (const part of formattedParts) {
      switch (part.type) {
        case 'currency':
        case 'literal':
          {
            if (integer.length === 0) {
              symbol.push(part.value);
            } else {
              decimal.push(part.value);
            }
            break;
          }
        case 'integer':
        case 'group':
          {
            integer.push(part.value);
            break;
          }
        case 'decimal':
          {
            separator = part.value;
            break;
          }
        case 'fraction':
          {
            decimal.push(part.value);
            break;
          }
        default:
          break;
      }
    }
    return {
      parts: {
        symbol: symbol.join(''),
        integer: integer.join(''),
        separator,
        decimal: decimal.join('')
      },
      formattedPrice: formatted
    };
  }, [valueInMinorUnits, locale, currency]);
  const accessiblePrice = srText ?? formattedPrice;
  return <span className={className} {...props} data-sentry-component="StyledPrice" data-sentry-source-file="StyledPrice.tsx">
      <span className="sr-only">{accessiblePrice}</span>

      <span aria-hidden="true">
        {!!parts.symbol && <span className={classNames.symbol}>{parts.symbol}</span>}
        <span className={classNames.integer}>{parts.integer}</span>
        {!!parts.separator && <span className={classNames.separator}>{parts.separator}</span>}
        {!!parts.decimal && <span className={classNames.decimal}>{parts.decimal}</span>}
      </span>
    </span>;
};
export default StyledPrice;